import {
	Checkbox,
	DatePicker,
	ExpandButton,
	FormattedText,
	FormItem,
	InputWithMoreInfo,
	NumberInput,
	Select,
	SelectItem,
	TextInput,
	useAlertDialog,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { IObjectInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import {
	AttendeeIdWithKind,
	EventSaveModel,
	EventStatus,
	LocationType,
	TicketType
} from "client/Api/EventsManagement/eventsManagement";
import styles from "./DetailsStep.module.less";
import { InputValidationResultKind } from "client/Validation";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNil, isNotNil } from "client/Misc/utils";
import {
	addTime,
	areOnTheSameDay,
	isValidDate
} from "client/DateAndTime/dateTimeUtils";
import { useSynchronizedRef } from "client/Hooks";
import { getEventDurationMs } from "client/Features/Calendar/DateSelect";
import { isEndTimeAfterStart } from "./EditEventDialog";

const {
	Title,
	Step,
	Content,
	FormContainer
} = HubItemComposerDialog;

export type DetailsStepProps = {
	isActive: boolean;
	attachable: IObjectInputAttachable<EventSaveModel>;
	status: EventStatus;
	timezone: TimeZone;
	attendeeIdsWithKind: readonly AttendeeIdWithKind[];
	infoAlerts?: ReactElement;
	isDuplicate: boolean;
};

export const DetailsStep = (props: DetailsStepProps): ReactElement => {
	const {
		isActive,
		attachable,
		status,
		timezone,
		attendeeIdsWithKind,
		infoAlerts,
		isDuplicate
	} = props;

	const startValue: Date | null = attachable.for("startTime")
		.value;
	const endValue: Date | null = attachable.for("endTime")
		.value;

	const lastValidEndRef: RefObject<Date | null> = useRef<Date | null>(null);

	const startDateChangeHandler = useCallback(
		(value: Date | null): void => {
			if (isNil(value) || !isValidDate(value)) {
				return;
			}

			if (
				isNil(startValue)
					&& isNotNil(endValue)
					&& isEndTimeAfterStart(value, endValue)
			) {
				lastValidEndRef.current = endValue;

				return;
			}

			const newEndValue = new Date(
				value.getTime() + getEventDurationMs(startValue, endValue)
			);

			lastValidEndRef.current = newEndValue;

			attachable.for("endTime")
				.reducer
				.setValue(newEndValue);

			attachable.for("endTime")
				.validation
				.triggerInputFinished();
		},
		[
			attachable,
			startValue,
			endValue
		]
	);

	const endDateChangeHandler = useCallback(
		(value: Date | null): void => {
			if ((isNil(startValue) || !isValidDate(startValue)) && isValidDate(value)) {
				lastValidEndRef.current = value;

				return;
			}

			if (isEndTimeAfterStart(startValue, value)) {
				lastValidEndRef.current = value;

				return;
			}

			let newEndValue: Date | null;

			if (
				isNotNil(startValue)
					&& isNotNil(value)
					&& isValidDate(value)
					&& areOnTheSameDay(
						value,
						startValue,
						timezone
					)
			) {
				newEndValue = value.getTime() < startValue.getTime()
					? addTime(
						startValue,
						30,
						"minute"
					)
					: value;
			} else {
				newEndValue = isNotNil(lastValidEndRef.current)
					? lastValidEndRef.current
					: value;
			}

			lastValidEndRef.current = newEndValue;

			attachable.for("endTime")
				.reducer
				.setValue(newEndValue);

			attachable.for("endTime")
				.validation
				.triggerInputFinished();
		},
		[
			attachable,
			startValue,
			timezone
		]
	);

	const [shouldRenderAdvanced, setShouldRenderAdvanced] = useState<boolean>(false);

	const modelRef: ReadonlyRefObject<EventSaveModel> = useSynchronizedRef(attachable.value);

	useEffect(
		(): void => {
			if (!isActive) {
				return;
			}

			setShouldRenderAdvanced(
				initialShouldRenderAdvanced(
					modelRef.current,
					isDuplicate
				)
			);
		},
		[
			isActive,
			isDuplicate,
			modelRef
		]
	);

	const [attendanceInfoDialog, openAttendanceInfoDialog] = useAlertDialog();
	const [confirmRemoveAttendeesDialog, openConfirmRemoveAttendeesDialog] = useConfirmationDialog();

	const isArchived: boolean = status === EventStatus.Archived;
	const locationType: LocationType | null = attachable.value
		.locationType;
	const isHybrid: boolean = locationType === LocationType.Hybrid;

	const hasErrorInAdvancedSettings: boolean = useMemo(
		(): boolean => {
			const advancedFields: readonly StringKeyOf<EventSaveModel>[] = [
				"ticketType",
				"signUpDeadline",
				"availableSpots",
				"externalUrl"
			];

			return advancedFields.some(
				x => attachable.for(x)
					.validation
					.getValidationState().kind === InputValidationResultKind.Invalid
			);
		},
		[attachable]
	);

	const clickHandler = useCallback(
		(): void => {
			if (!hasErrorInAdvancedSettings) {
				setShouldRenderAdvanced(status => !status);
			}
		},
		[hasErrorInAdvancedSettings]
	);

	const attendanceToggleHandler = useCallback(
		async (isAttendanceEnabled: boolean): Promise<void> => {
			if (!isAttendanceEnabled && attendeeIdsWithKind.length > 0) {
				const result: TryResult<void> = await openConfirmRemoveAttendeesDialog({
					title: localize("_EventHasAttendees"),
					confirmText: localize("_DisableAttendance"),
					children: localize("_EventAttendeesRemoveInfoMessage"),
					trackingId: "disable-event-attendance-confirmation-dialog"
				});

				if (!result.wasSuccessful) {
					return;
				}
			}

			attachable.for("isAttendanceEnabled")
				.reducer
				.setValue(isAttendanceEnabled);

			attachable.validation
				.triggerInputFinished();
		},
		[
			attachable,
			openConfirmRemoveAttendeesDialog,
			attendeeIdsWithKind
		]
	);

	const moreInfoHandler = useCallback(
		(): void => {
			openAttendanceInfoDialog({
				title: localize("_Attendance"),
				children: (
					<FormattedText
						text={localize("_EditEventAttendanceInfoMessage")}
						thingsToReplace={[]}
					/>
				),
				showCloseButton: true,
				trackingId: "event-attendance-info-dialog"
			});
		},
		[openAttendanceInfoDialog]
	);

	useEffect(
		() => {
			if (hasErrorInAdvancedSettings && !shouldRenderAdvanced) {
				setShouldRenderAdvanced(true);
			}
		},
		[
			attachable.validation,
			hasErrorInAdvancedSettings,
			shouldRenderAdvanced
		]
	);

	return (
		<>
			<Step
				isActive={isActive}
				trackingId="details-step"
			>
				<Content>
					{infoAlerts}
					<FormContainer>
						<Title text={localize("_EventDetails")} />
						<FormItem
							label={localize("_EventType")}
							isRequired
						>
							<Select
								{...attachable.for("locationType").attachToSelect()}
								options={eventTypeOptions}
								placeholder={localize("_ChooseEventType")}
								isDisabled={isArchived}
								menuTitle={localize("_EventType")}
							/>
							<ValidationError {...attachable.for("locationType").attachToValidationError()} />
						</FormItem>
						{(locationType === LocationType.Physical || isHybrid) && (
							<FormItem
								label={localize("_Location")}
							>
								<TextInput
									{...attachable.for("location").attachToTextInput()}
									placeholder={localize("_EnterLocation")}
									isDisabled={isArchived}
								/>
								<ValidationError {...attachable.for("location").attachToValidationError()} />
							</FormItem>
						)}
						{(locationType === LocationType.Online || isHybrid) && (
							<FormItem
								label={localize("_OnlineMeetingUrl")}
							>
								<TextInput
									{...attachable.for("onlineEventUrl").attachToTextInput()}
									placeholder={localize("_EnterUrl")}
									isDisabled={isArchived}
								/>
								<ValidationError {...attachable.for("onlineEventUrl").attachToValidationError()} />
							</FormItem>
						)}
						<FormItem
							label={localize("_StartEvent")}
							isRequired
						>
							<DatePicker
								{...attachable.for("startTime")
									.attachToDatePicker()
									.withOnChange(startDateChangeHandler)
								}
								isDisabled={isArchived}
								timezone={timezone}
							/>
							<ValidationError {...attachable.for("startTime").attachToValidationError()} />
						</FormItem>
						<FormItem
							label={localize("_EndEvent")}
							isRequired
						>
							<DatePicker
								{...attachable.for("endTime")
									.attachToDatePicker()
									.withOnChange(endDateChangeHandler)
								}
								isDisabled={isArchived}
								timezone={timezone}
								minDate={attachable.for("startTime").value ?? undefined}
							/>
							<ValidationError {...attachable.for("endTime").attachToValidationError()} />
						</FormItem>
						<div className={styles.moreButton}>
							<ExpandButton
								text={localize("_AdvancedSettings")}
								onClick={clickHandler}
								isExpanded={shouldRenderAdvanced}
								trackingId="advanced-settings"
							/>
						</div>
						{shouldRenderAdvanced && (
							<>
								<FormItem
									label={localize("_TicketType")}
								>
									<Select
										{...attachable.for("ticketType").attachToSelect()}
										options={ticketTypeOptions}
										placeholder={localize("_ChooseTicketType")}
										isDisabled={isArchived}
										menuTitle={localize("_TicketType")}
									/>
									<ValidationError {...attachable.for("ticketType").attachToValidationError()} />
								</FormItem>
								<FormItem
									label={localize("_Attendance")}
								>
									<InputWithMoreInfo onMoreInfo={moreInfoHandler}>
										<Checkbox
											trackingId={attachable
												.for("isAttendanceEnabled")
												.attachToCheckbox()
												.trackingId
											}
											value={attachable
												.value
												.isAttendanceEnabled
											}
											onChange={attendanceToggleHandler}
											text={localize("_AllowUsersToAttendTheEvent")}
											isDisabled={isArchived}
										/>
									</InputWithMoreInfo>
									<ValidationError {...attachable.for("isAttendanceEnabled").attachToValidationError()} />
								</FormItem>
								{attachable.when(
									x => x.isAttendanceEnabled,
									(handler) => (
										<>
											<FormItem
												label={localize("_SignUpBefore")}
											>
												<DatePicker
													{...handler.for("signUpDeadline").attachToDatePicker()}
													isDisabled={isArchived}
													timezone={timezone}
												/>
												<ValidationError {...handler.for("signUpDeadline").attachToValidationError()} />
											</FormItem>
											<FormItem label={localize("_AvailableSpots")}>
												<NumberInput
													{...handler.for("availableSpots").attachToTextInput()}
													placeholder={localize("_EnterNumberOfAvailableSpots")}
													isDisabled={isArchived}
												/>
												<ValidationError {...handler.for("availableSpots").attachToValidationError()} />
											</FormItem>
										</>
									)
								)}
								<FormItem label={localize("_ExternalEventUrl")}>
									<TextInput
										{...attachable.for("externalUrl").attachToTextInput()}
										placeholder={localize("_EnterUrl")}
										isDisabled={isArchived}
									/>
									<ValidationError {...attachable.for("externalUrl").attachToValidationError()} />
								</FormItem>
							</>
						)}
					</FormContainer>
				</Content>
			</Step>
			{attendanceInfoDialog}
			{confirmRemoveAttendeesDialog}
		</>
	);
};
DetailsStep.displayName = "DetailsStep";

const eventTypeOptions: readonly SelectItem<LocationType>[] = [
	{
		text: localize("_Online"),
		value: LocationType.Online
	},
	{
		text: localize("_Physical"),
		value: LocationType.Physical
	},
	{
		text: localize("_Hybrid"),
		value: LocationType.Hybrid
	},
];

const ticketTypeOptions: readonly SelectItem<TicketType | null>[] = [
	{
		text: localize("_None"),
		value: null
	},
	{
		text: localize("_(Cost)Free"),
		value: TicketType.Free
	},
	{
		text: localize("_Paid"),
		value: TicketType.Paid
	}
];

function initialShouldRenderAdvanced(
	model: EventSaveModel,
	isDuplicate: boolean
): boolean {
	if (!isDuplicate) {
		return false;
	}

	const areAdvancedSettingsSetToDefault: boolean = isNil(model.ticketType)
		&& model.isAttendanceEnabled
		&& isNil(model.signUpDeadline)
		&& isNil(model.availableSpots)
		&& !model.externalUrl;

	return !areAdvancedSettingsSetToDefault;
}